import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Wand2, Plus, Trash2, Copy, Check, AlertCircle, 
  Code, FileText, Hash, AtSign, Globe, Lock, Key,
  Database, Bug, Shield, Eye, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// Predefined pattern categories for security testing
const PATTERN_CATEGORIES = {
  sql_errors: {
    name: "SQL Error Messages",
    icon: Database,
    patterns: [
      { label: "MySQL Syntax Error", value: "SQL.*syntax", description: "Matches MySQL syntax errors" },
      { label: "MySQL Function Warning", value: "mysql_fetch|mysql_query|mysql_connect", description: "Matches MySQL function warnings" },
      { label: "Oracle Error", value: "ORA-[0-9]+", description: "Matches Oracle error codes" },
      { label: "PostgreSQL Error", value: "PG::.*Error|pg_query", description: "Matches PostgreSQL errors" },
      { label: "MSSQL Error", value: "ODBC SQL Server Driver|SQLServer", description: "Matches MSSQL errors" },
      { label: "SQLite Error", value: "sqlite_|SQLite3::", description: "Matches SQLite errors" },
      { label: "SQLSTATE Error", value: "SQLSTATE\\[[0-9]+\\]", description: "Matches SQLSTATE codes" },
    ]
  },
  xss_patterns: {
    name: "XSS Indicators",
    icon: Code,
    patterns: [
      { label: "Script Tags", value: "<script[^>]*>", description: "Matches script tag injection" },
      { label: "Event Handlers", value: "on(error|load|click|mouseover)\\s*=", description: "Matches JS event handlers" },
      { label: "JavaScript Protocol", value: "javascript:", description: "Matches javascript: URLs" },
      { label: "Data URI", value: "data:\\s*text/html", description: "Matches data: HTML URIs" },
      { label: "SVG Injection", value: "<svg[^>]*on", description: "Matches SVG with events" },
      { label: "Expression CSS", value: "expression\\s*\\(", description: "Matches CSS expressions (IE)" },
    ]
  },
  sensitive_data: {
    name: "Sensitive Data Exposure",
    icon: Eye,
    patterns: [
      { label: "API Key Pattern", value: "api[_-]?key\\s*[=:]\\s*[a-zA-Z0-9_-]{20,}", description: "Matches exposed API keys" },
      { label: "AWS Access Key", value: "AKIA[A-Z0-9]{16}", description: "Matches AWS access key IDs" },
      { label: "AWS Secret Key", value: "aws[_-]?secret[_-]?access[_-]?key", description: "Matches AWS secret references" },
      { label: "Private Key", value: "-----BEGIN (RSA |DSA |EC )?PRIVATE KEY-----", description: "Matches PEM private keys" },
      { label: "Password Field", value: "password\\s*[=:]\\s*['\"][^'\"]+['\"]", description: "Matches hardcoded passwords" },
      { label: "JWT Token", value: "eyJ[a-zA-Z0-9_-]*\\.eyJ[a-zA-Z0-9_-]*\\.", description: "Matches JWT tokens" },
      { label: "Credit Card", value: "\\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14})\\b", description: "Matches credit card numbers" },
    ]
  },
  command_injection: {
    name: "Command Injection",
    icon: Bug,
    patterns: [
      { label: "Unix User ID", value: "uid=[0-9]+", description: "Matches Unix uid output" },
      { label: "Unix passwd File", value: "root:.*:0:0", description: "Matches /etc/passwd content" },
      { label: "Windows Directory", value: "Volume Serial Number|Directory of", description: "Matches Windows dir output" },
      { label: "Shell Error", value: "(sh|bash|cmd):\\s", description: "Matches shell error messages" },
      { label: "Command Not Found", value: "command not found|not recognized", description: "Matches command errors" },
    ]
  },
  path_traversal: {
    name: "Path Traversal",
    icon: FileText,
    patterns: [
      { label: "Unix passwd Content", value: "root:[x*]:0:0", description: "Matches /etc/passwd format" },
      { label: "Windows INI File", value: "\\[boot loader\\]|\\[fonts\\]", description: "Matches Windows INI files" },
      { label: "Web Config", value: "<configuration>|<appSettings>", description: "Matches .NET web.config" },
    ]
  },
  cloud_metadata: {
    name: "Cloud Metadata (SSRF)",
    icon: Globe,
    patterns: [
      { label: "AWS Metadata", value: "ami-id|instance-id|iam/security-credentials", description: "Matches AWS metadata" },
      { label: "GCP Metadata", value: "computeMetadata|project-id", description: "Matches GCP metadata" },
      { label: "Azure Metadata", value: "azureenvironment|subscriptionId", description: "Matches Azure metadata" },
    ]
  },
  auth_errors: {
    name: "Authentication Errors",
    icon: Lock,
    patterns: [
      { label: "Invalid Credentials", value: "invalid (username|password|credentials)", description: "Matches auth failures" },
      { label: "Access Denied", value: "access denied|unauthorized|forbidden", description: "Matches access errors" },
      { label: "Session Expired", value: "session (expired|invalid|timeout)", description: "Matches session errors" },
    ]
  }
};

// Building blocks for custom regex
const REGEX_BUILDING_BLOCKS = {
  characters: [
    { label: "Any character", value: ".", description: "Matches any single character" },
    { label: "Digit (0-9)", value: "\\d", description: "Matches any digit" },
    { label: "Non-digit", value: "\\D", description: "Matches any non-digit" },
    { label: "Word char (a-z, 0-9, _)", value: "\\w", description: "Matches word characters" },
    { label: "Non-word char", value: "\\W", description: "Matches non-word characters" },
    { label: "Whitespace", value: "\\s", description: "Matches whitespace" },
    { label: "Non-whitespace", value: "\\S", description: "Matches non-whitespace" },
  ],
  quantifiers: [
    { label: "Zero or more (*)", value: "*", description: "0 or more times" },
    { label: "One or more (+)", value: "+", description: "1 or more times" },
    { label: "Zero or one (?)", value: "?", description: "0 or 1 time" },
    { label: "Exactly n times", value: "{n}", description: "Exactly n times" },
    { label: "n or more times", value: "{n,}", description: "n or more times" },
    { label: "Between n and m", value: "{n,m}", description: "Between n and m times" },
  ],
  anchors: [
    { label: "Start of string", value: "^", description: "Match at start" },
    { label: "End of string", value: "$", description: "Match at end" },
    { label: "Word boundary", value: "\\b", description: "Match at word boundary" },
  ],
  groups: [
    { label: "Capture group", value: "()", description: "Captures matched text" },
    { label: "Non-capture group", value: "(?:)", description: "Groups without capturing" },
    { label: "OR operator", value: "|", description: "Matches either pattern" },
    { label: "Character class", value: "[]", description: "Match any char in set" },
    { label: "Negated class", value: "[^]", description: "Match any char NOT in set" },
  ]
};

const RegexBuilder = ({ isOpen, onClose, onApply, initialPattern = '' }) => {
  const [activeTab, setActiveTab] = useState('presets');
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  const [customParts, setCustomParts] = useState([]);
  const [testString, setTestString] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [manualPattern, setManualPattern] = useState(initialPattern);
  const [combineMode, setCombineMode] = useState('or'); // 'or' or 'sequence'

  // Generate final regex from selected patterns
  const generateRegex = () => {
    if (activeTab === 'manual') {
      return manualPattern;
    }
    
    const allPatterns = [...selectedPatterns.map(p => p.value), ...customParts.filter(p => p)];
    
    if (allPatterns.length === 0) return '';
    if (allPatterns.length === 1) return allPatterns[0];
    
    if (combineMode === 'or') {
      return allPatterns.join('|');
    } else {
      return allPatterns.join('.*');
    }
  };

  const finalPattern = generateRegex();

  // Test the pattern against test string
  useEffect(() => {
    if (!finalPattern || !testString) {
      setTestResult(null);
      return;
    }

    try {
      const regex = new RegExp(finalPattern, 'gi');
      const matches = testString.match(regex);
      setTestResult({
        valid: true,
        matches: matches || [],
        count: matches ? matches.length : 0
      });
    } catch (e) {
      setTestResult({
        valid: false,
        error: e.message
      });
    }
  }, [finalPattern, testString]);

  const togglePattern = (pattern) => {
    const exists = selectedPatterns.find(p => p.value === pattern.value);
    if (exists) {
      setSelectedPatterns(selectedPatterns.filter(p => p.value !== pattern.value));
    } else {
      setSelectedPatterns([...selectedPatterns, pattern]);
    }
  };

  const addCustomPart = () => {
    setCustomParts([...customParts, '']);
  };

  const updateCustomPart = (index, value) => {
    const newParts = [...customParts];
    newParts[index] = value;
    setCustomParts(newParts);
  };

  const removeCustomPart = (index) => {
    setCustomParts(customParts.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    if (!finalPattern) {
      toast.error('Please create a pattern first');
      return;
    }
    onApply(finalPattern);
    onClose();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(finalPattern);
    toast.success('Pattern copied to clipboard');
  };

  const clearAll = () => {
    setSelectedPatterns([]);
    setCustomParts([]);
    setManualPattern('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            Regex Pattern Builder
          </DialogTitle>
          <DialogDescription>
            Build regex patterns visually using presets or custom building blocks
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="presets">Security Presets</TabsTrigger>
            <TabsTrigger value="builder">Visual Builder</TabsTrigger>
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-hidden mt-4">
            {/* Presets Tab */}
            <TabsContent value="presets" className="h-full overflow-auto m-0">
              <div className="space-y-4 pr-2">
                {Object.entries(PATTERN_CATEGORIES).map(([key, category]) => {
                  const Icon = category.icon;
                  return (
                    <Card key={key}>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {category.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="grid grid-cols-2 gap-2">
                          {category.patterns.map((pattern, idx) => {
                            const isSelected = selectedPatterns.find(p => p.value === pattern.value);
                            return (
                              <div
                                key={idx}
                                onClick={() => togglePattern(pattern)}
                                className={`p-2 rounded-lg border cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">{pattern.label}</span>
                                  {isSelected && <Check className="w-4 h-4 text-blue-500" />}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{pattern.description}</p>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Visual Builder Tab */}
            <TabsContent value="builder" className="h-full overflow-auto m-0">
              <div className="space-y-4 pr-2">
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Building Blocks</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-4">
                    {Object.entries(REGEX_BUILDING_BLOCKS).map(([key, blocks]) => (
                      <div key={key}>
                        <Label className="text-xs uppercase text-gray-500 mb-2 block">
                          {key.replace('_', ' ')}
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {blocks.map((block, idx) => (
                            <Button
                              key={idx}
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const newParts = [...customParts, block.value];
                                setCustomParts(newParts);
                              }}
                              title={block.description}
                            >
                              <code className="text-xs">{block.label}</code>
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">Custom Pattern Parts</CardTitle>
                      <Button size="sm" variant="outline" onClick={addCustomPart}>
                        <Plus className="w-4 h-4 mr-1" /> Add Part
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {customParts.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Click building blocks above or add custom parts
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {customParts.map((part, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Badge variant="outline" className="w-6 text-center">{index + 1}</Badge>
                            <Input
                              value={part}
                              onChange={(e) => updateCustomPart(index, e.target.value)}
                              placeholder="Enter pattern part..."
                              className="font-mono text-sm flex-1"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeCustomPart(index)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Manual Entry Tab */}
            <TabsContent value="manual" className="h-full m-0">
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Manual Regex Entry</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Input
                    value={manualPattern}
                    onChange={(e) => setManualPattern(e.target.value)}
                    placeholder="Enter your regex pattern..."
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Enter a raw regex pattern. Use backslashes for escaping (e.g., \d for digits).
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>

        {/* Combine Mode (for presets/builder) */}
        {activeTab !== 'manual' && (selectedPatterns.length > 1 || customParts.filter(p => p).length > 1) && (
          <div className="mt-4 flex items-center gap-4">
            <Label className="text-sm">Combine patterns with:</Label>
            <Select value={combineMode} onValueChange={setCombineMode}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="or">OR (match any)</SelectItem>
                <SelectItem value="sequence">Sequence (match all in order)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Generated Pattern Preview */}
        <Card className="mt-4 border-blue-200 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="py-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Generated Pattern</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={copyToClipboard} disabled={!finalPattern}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={clearAll}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <code className="block p-2 bg-white dark:bg-gray-900 rounded border text-sm font-mono break-all min-h-[40px]">
              {finalPattern || <span className="text-gray-400">No pattern generated yet</span>}
            </code>
          </CardContent>
        </Card>

        {/* Test Area */}
        <Card className="mt-4">
          <CardContent className="py-3">
            <Label className="text-sm font-medium mb-2 block">Test Your Pattern</Label>
            <Input
              value={testString}
              onChange={(e) => setTestString(e.target.value)}
              placeholder="Enter test string to check if pattern matches..."
              className="mb-2"
            />
            {testResult && (
              <div className={`p-2 rounded text-sm ${
                testResult.valid 
                  ? testResult.count > 0 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {testResult.valid ? (
                  testResult.count > 0 ? (
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {testResult.count} match{testResult.count > 1 ? 'es' : ''} found: {testResult.matches.slice(0, 3).join(', ')}
                      {testResult.matches.length > 3 && '...'}
                    </span>
                  ) : (
                    <span>No matches found in test string</span>
                  )
                ) : (
                  <span className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Invalid regex: {testResult.error}
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!finalPattern} className="bg-blue-600 hover:bg-blue-700">
            <Check className="w-4 h-4 mr-2" />
            Apply Pattern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegexBuilder;
